import React, { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

export type MultiSelectOption = {
  id: string | number;
  name: string;
};

export type MultiSelectProps = {
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  label?: ReactNode;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
};

export const MultiSelect = (props: MultiSelectProps) => {
  const {
    options,
    value = [],
    onValueChange,
    label,
    disabled = false,
    searchable = false,
    className,
    id,
    'aria-invalid': ariaInvalid,
  } = props;

  const { t } = useTranslation();

  const generatedId = useId();
  const selectId = id ?? generatedId;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOptions = useMemo(() => {
    return options.filter((option) => value.includes(String(option.id)));
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(lowerSearch),
    );
  }, [options, search, searchable]);

  const isFloating = open || selectedOptions.length > 0;

  const onSelectItemToggle = (optionId: string | number) => {
    const optionIdString = String(optionId);
    const newValue = value.includes(optionIdString)
      ? value.filter((id) => id !== optionIdString)
      : [...value, optionIdString];
    onValueChange(newValue);
  };

  const onBadgeRemove = (e: React.MouseEvent, optionId: string | number) => {
    e.stopPropagation();
    const optionIdString = String(optionId);
    const newValue = value.filter((id) => id !== optionIdString);
    onValueChange(newValue);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild>
          <Button
            id={selectId}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            disabled={disabled}
            className={cn(
              'dark:bg-card dark:hover:bg-card h-auto min-h-10 w-full justify-between border-2 border-transparent! bg-transparent py-2 font-normal shadow-none ring-0 outline-none hover:bg-transparent focus-visible:border-transparent! focus-visible:ring-0 aria-invalid:border-transparent! dark:border-transparent!',
            )}
          >
            {selectedOptions.length === 0 ? (
              <span className="flex-1 truncate text-left">&nbsp;</span>
            ) : (
              <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                {selectedOptions.map((option) => (
                  <Badge
                    key={String(option.id)}
                    className="max-w-full min-w-0"
                    onClick={(e) => onBadgeRemove(e, option.id)}
                  >
                    <span className="truncate">{option.name}</span>
                    <X className="ml-1 size-3 shrink-0" />
                  </Badge>
                ))}
              </div>
            )}
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
          onCloseAutoFocus={() => setSearch('')}
        >
          {searchable && (
            <div className="border-b p-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={label ? `${label}...` : 'Search...'}
                className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              />
            </div>
          )}
          <div className="max-h-75 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="text-muted-foreground py-6 text-center text-sm">
                {t('No options available')}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const optionIdString = String(option.id);
                const isSelected = value.includes(optionIdString);
                return (
                  <div
                    key={optionIdString}
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      "[&_svg:not([class*='text-'])]:text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                    )}
                    onClick={() => onSelectItemToggle(option.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onSelectItemToggle(option.id)}
                    />
                    <span className="flex-1 text-sm">{option.name}</span>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md bg-transparent px-[9px] transition-colors',
              open && !ariaInvalid && 'border-primary border-2',
              !open && !ariaInvalid && 'border-input border-2',
              ariaInvalid && 'border-destructive border-2',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={selectId}
            className={cn(
              'bg-card text-muted-foreground pointer-events-none absolute left-[13px] z-2 gap-1 rounded-xs px-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 -translate-y-1/2 text-xs'
                : 'top-1/2 -translate-y-1/2 text-sm',
              ariaInvalid ? 'text-destructive' : open && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};
